import { Checkbox, Radio, RadioGroup } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { SearchAndFilterTypes } from "src/constants";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as GQL from "src/types/graphql";
import { GET_PROGRAMS_FOR_CAPACITIES_FILTERS } from "../../graphql/queries";
import { CommonFilterProps, FormFilter, Option } from "../CommonFilters";

// Note: This value must match the backend logic.
const NO_PROGRAM_ID = "1";

export const CapacityFilter = ({ filterSettings }: CommonFilterProps) => {
  const organization = useOrganization();

  const [program, setProgram] = useState(
    filterSettings.getOne(SearchAndFilterTypes.WithinCapacity)
  );
  // Note: This doesn't reset if the filters are applied without changes.
  useEffect(() => {
    setProgram(filterSettings.getOne(SearchAndFilterTypes.WithinCapacity));
  }, [filterSettings]);

  const { remoteData: getProgramsRemoteData } = useRemoteDataQuery<
    GQL.GetProgramsForCapacitiesFilter,
    GQL.GetProgramsForCapacitiesFilterVariables
  >(GET_PROGRAMS_FOR_CAPACITIES_FILTERS, {
    skip: !organization.hasData(),
    variables: {
      organization_id: organization.toNullable()?.id ?? "",
    },
  });

  const capacityOptions = useMemo<Option[]>(
    // Assume there is only 0 or 1 program group for an organization
    () => getProgramsRemoteData.toNullable()?.program_group[0]?.programs ?? [],
    [getProgramsRemoteData]
  );

  const handleCapacityFilterChange = ({
    target: { checked },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!checked) {
      setProgram(null);
      filterSettings.clear(SearchAndFilterTypes.WithinCapacity);
    } else if (capacityOptions.length) {
      // org has programs; wait until one is selected
      setProgram("");
    } else {
      // org does not have programs
      setProgram(NO_PROGRAM_ID);
      filterSettings.set(SearchAndFilterTypes.WithinCapacity, NO_PROGRAM_ID);
    }
  };

  const setProgramForCapacityFilter = (programId: string) => {
    setProgram(programId);
    filterSettings.set(SearchAndFilterTypes.WithinCapacity, programId);
  };

  return (
    <FormFilter label="Capacity">
      <Checkbox
        isChecked={program !== null}
        onChange={handleCapacityFilterChange}
      >
        Show only within capacity
      </Checkbox>
      {program !== null && capacityOptions.length && (
        <RadioGroup
          display="flex"
          flexDirection="column"
          gap="2"
          value={program || undefined}
          onChange={setProgramForCapacityFilter}
        >
          {capacityOptions.map((option) => (
            <Radio key={option.id} value={option.id} ml={4}>
              {option.label}
            </Radio>
          ))}
        </RadioGroup>
      )}
    </FormFilter>
  );
};
